import type { CurrentUser } from '@zhiyue/core';
import { useSession } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const BUFFER = 60_000;
export type OpenPlatformTokenResult = {
  accessToken: string;
  expiresAt: number;
};
type TokenContext = Pick<CurrentUser, 'tenantUuid' | 'memberUuid'> & {
  organizationUuid?: string;
};
const keyOf = (user?: TokenContext | null) =>
  [user?.tenantUuid, user?.organizationUuid, user?.memberUuid]
    .map((v) => String(v || '').trim())
    .join('|');
let activeKey = '';
let generation = 0;
let token: OpenPlatformTokenResult | undefined;
let pending: Promise<OpenPlatformTokenResult> | undefined;
const listeners = new Set<() => void>();
const emit = () =>
  listeners.forEach((fn) => {
    fn();
  });
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
export const clearOpenPlatformToken = () => {
  generation += 1;
  activeKey = '';
  token = undefined;
  pending = undefined;
  // Discard tokens persisted by the previous integration.
  try {
    sessionStorage.removeItem('crm.openPlatformAccessToken');
  } catch {
    /* storage unavailable */
  }
  emit();
};
export async function fetchOpenPlatformToken(
  user: TokenContext,
  rejectedToken?: string,
): Promise<OpenPlatformTokenResult> {
  const key = keyOf(user);
  if (!key.split('|').every(Boolean))
    throw new Error('当前会话缺少开放平台认证信息');
  if (activeKey !== key) {
    generation += 1;
    activeKey = key;
    token = undefined;
    pending = undefined;
    emit();
  }
  if (
    token &&
    token.expiresAt > Date.now() + BUFFER &&
    (!rejectedToken || token.accessToken !== rejectedToken)
  )
    return token;
  if (pending) return pending;
  const version = generation;
  const promise = request<unknown>('/bff/open-platform/token', {
    method: 'GET',
    skipErrorHandler: true,
    headers: rejectedToken
      ? { 'x-rejected-m2m-token': rejectedToken }
      : undefined,
  })
    .then((response) => {
      const envelope = response as { data?: unknown };
      const value = (envelope?.data ||
        response) as Partial<OpenPlatformTokenResult>;
      if (
        typeof value.accessToken !== 'string' ||
        !value.accessToken.trim() ||
        typeof value.expiresAt !== 'number' ||
        !Number.isFinite(value.expiresAt) ||
        value.expiresAt <= Date.now() + BUFFER
      )
        throw new Error('开放平台 Token 响应无效');
      if (version !== generation || key !== activeKey)
        throw new Error('会话已切换');
      token = { accessToken: value.accessToken, expiresAt: value.expiresAt };
      emit();
      return token;
    })
    .finally(() => {
      if (pending === promise) pending = undefined;
    });
  pending = promise;
  return promise;
}
export const useOpenPlatformRuntime = () => {
  const { currentUser } = useSession();
  const key = keyOf(currentUser);
  const cached = useSyncExternalStore(subscribe, () =>
    activeKey === key ? token : undefined,
  );
  const [error, setError] = useState<Error>();
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setError(undefined);
    setAttempt((v) => v + 1);
  }, []);
  useEffect(() => {
    let cancelled = false;
    setError(undefined);
    if (!currentUser || !key.split('|').every(Boolean)) {
      clearOpenPlatformToken();
      return;
    }
    void fetchOpenPlatformToken(currentUser).catch((err) => {
      if (!cancelled)
        setError(err instanceof Error ? err : new Error('认证失败'));
    });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);
  const config = useMemo(() => {
    const gatewayBaseUrl = [
      BizAppConfig.appOpenServiceUrl,
      BizAppConfig.appOpenApiGatewayPath,
    ]
      .map((v) =>
        String(v || '')
          .trim()
          .replace(/\/+$/, ''),
      )
      .filter(Boolean)
      .join('/')
      .replace(/([^:]\/)\/+/g, '$1');
    if (!gatewayBaseUrl || !cached || !currentUser?.memberUuid)
      return undefined;
    return {
      gatewayBaseUrl,
      accessToken: cached.accessToken,
      m2mToken: cached.accessToken,
      memberUuid: currentUser.memberUuid,
      refreshM2mToken: async (rejectedM2mToken: string) => {
        if (key !== activeKey) throw new Error('会话已切换');
        const result = await fetchOpenPlatformToken(
          currentUser,
          rejectedM2mToken,
        );
        return result.accessToken;
      },
    };
  }, [cached, key]);
  return { config, error, retry, contextKey: key };
};
export const useOpenPlatformConfig = () => useOpenPlatformRuntime().config;
